import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Thermometer, Calendar, GripVertical, X } from "lucide-react";
import CompressorTempChartSimple from './compressor-temp-chart-simple';
import { apiClient } from '@/api/client';

interface CompressorTempData {
  time: string;
  stat_type: string;
  sensor_kit_serial_number: string;
  temp_c: number;
  predicted_max_temp_c?: number;
}

interface DraggableCompressorTempCardProps {
  installationId: string;
  locationName: string;
  sensorKitSerialNumber: string;
  maxDurationMinutes?: number;
  onRemove?: () => void;
  className?: string;
}

const DraggableCompressorTempCard: React.FC<DraggableCompressorTempCardProps> = ({ 
  installationId,
  locationName,
  sensorKitSerialNumber,
  maxDurationMinutes = 2,
  onRemove,
  className = ""
}) => {
  const [data, setData] = useState<CompressorTempData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<string>('7d');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const daysBack = parseInt(timeRange.replace('d', ''));
      const end = new Date();
      const start = new Date(end.getTime() - daysBack * 24 * 60 * 60 * 1000);

      const response = await apiClient.post('on-periods/sensor-temps/', {
        installation_id: installationId,
        start: start.toISOString(),
        end: end.toISOString(),
        sensor_name: 'Compressor outlet',
        aggregation: 'daily',
        stat_type: 'max',
        min_duration_minutes: maxDurationMinutes,
      });

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      const mapped = (response.data.data || []).map((row: any) => ({
        time: row.time,
        stat_type: 'max',
        sensor_kit_serial_number: sensorKitSerialNumber,
        temp_c: row.temp_c,
        predicted_max_temp_c: row.predicted_warning_temp_c,
      }));
      setData(mapped);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [installationId, locationName, sensorKitSerialNumber, maxDurationMinutes, timeRange]);

  // Transform data for chart - dates come as "2025-09-26"
  const chartData = data.map(item => ({
    x: new Date(item.time + 'T12:00:00Z').getTime(), // Set to noon UTC for consistent plotting
    y: typeof item.temp_c === 'string' ? parseFloat(item.temp_c) * 9/5 + 32 : item.temp_c * 9/5 + 32,
    temp_c: typeof item.temp_c === 'string' ? parseFloat(item.temp_c) : item.temp_c,
    sensor_kit: item.sensor_kit_serial_number
  }));

  // Calculate statistics
  const temps = chartData.map(d => d.y);
  const avgTemp = temps.length > 0 ? temps.reduce((sum, temp) => sum + temp, 0) / temps.length : 0;
  const maxTemp = temps.length > 0 ? Math.max(...temps) : 0;
  const minTemp = temps.length > 0 ? Math.min(...temps) : 0;


  return (
    <Card className={`relative ${className}`}>
      {/* Drag Handle */}
      <div className="absolute top-2 left-2 cursor-move opacity-50 hover:opacity-100 transition-opacity">
        <GripVertical className="h-4 w-4 text-gray-400" />
      </div>

      {/* Remove Button */}
      {onRemove && (
        <Button
          variant="ghost"
          size="sm"
          className="absolute top-2 right-2 h-6 w-6 p-0"
          onClick={onRemove}
        >
          <X className="h-3 w-3" />
        </Button>
      )}

      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="ml-6">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Thermometer className="h-5 w-5" />
              Daily Maximum Compressor Outlet Temperature
            </CardTitle>
            <CardDescription className="mt-1">
              {locationName} • Serial: {sensorKitSerialNumber} • One point per day
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchData}
              disabled={loading}
            >
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {/* Controls */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">7 Days</SelectItem>
                <SelectItem value="14d">14 Days</SelectItem>
                <SelectItem value="30d">30 Days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {error ? (
          <div className="text-center py-8">
            <div className="text-red-500 mb-2">Error loading data</div>
            <div className="text-sm text-muted-foreground">{error}</div>
            <Button variant="outline" onClick={fetchData} className="mt-4">
              Try Again
            </Button>
          </div>
        ) : loading ? (
          <div className="text-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
            <div className="text-muted-foreground">Loading compressor temperature data...</div>
          </div>
        ) : chartData.length === 0 ? (
          <div className="text-center py-8">
            <Thermometer className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <div className="text-muted-foreground">No compressor temperature data found</div>
            <div className="text-sm text-muted-foreground mt-2">
              No cycles detected for installation {installationId} in the past {timeRange}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Reference Temperature Box */}
            {data.length > 0 && data[data.length - 1].predicted_max_temp_c && (
              <div className="p-3 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-yellow-900">Predicted Max Temperature</div>
                    <div className="text-xs text-yellow-700">Warning Zone Reference</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-yellow-900">
                      {((data[data.length - 1].predicted_max_temp_c || 0) * 9/5 + 32).toFixed(1)}°F
                    </div>
                    <div className="text-xs text-yellow-700">
                      Critical: {(((data[data.length - 1].predicted_max_temp_c || 0) * 9/5 + 32) * 1.2).toFixed(1)}°F
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Summary Statistics */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-xl font-bold text-blue-600">{chartData.length}</div>
                <div className="text-xs text-blue-800">Days</div>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded-lg">
                <div className="text-xl font-bold text-orange-600">{avgTemp.toFixed(1)}°F</div>
                <div className="text-xs text-orange-800">Avg Daily Max</div>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <div className="text-xl font-bold text-red-600">{maxTemp.toFixed(1)}°F</div>
                <div className="text-xs text-red-800">Peak</div>
              </div>
            </div>

            {/* Interactive Chart */}
            <div className="h-80">
              <CompressorTempChartSimple
                data={data}
                timeRange={timeRange}
                selectedStatType="max"
              />
            </div>

            {/* Chart Controls */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900 text-sm">Interactive Controls</h4>
                <div className="text-xs text-gray-600">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Drag & Zoom
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span><strong>Mouse Wheel:</strong> Zoom</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span><strong>Click & Drag:</strong> Pan</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DraggableCompressorTempCard;
